"use client";

import { AuthLink } from "@/components/auth-link";
import { Icons } from "@/components/icons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useUser } from "@/lib/auth";
import { axiosInstance } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { LinkIcon, Share2Icon } from "lucide-react";
import { toast } from "sonner";

export function ShareButton({ url }: { url: string }) {
  const { user } = useUser();

  let text = url;

  if (user?.affiliate_code) {
    text = `${url}?ref=${user.affiliate_code}`;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button">
          <Share2Icon className="w-5 h-5 text-theme-gray" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-[540px]" placement="bottom">
        <DialogHeader>
          <DialogTitle className="text-lg dark:text-theme-dark">Bagikan Event</DialogTitle>
        </DialogHeader>
        <DialogDescription className="pt-4">
          Yuk ajak teman-temanmu dengan membagikan event ini di media sosialmu.
        </DialogDescription>
        <div className="pt-8">
          <ShareContent text={text} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShareContent({ text }: { text: string }) {
  const { user } = useUser();

  const {
    data: affiliate,
    error,
    isSuccess,
    isLoading,
  } = useQuery(["affilate", "overview"], async () => {
    const data = await axiosInstance.get("/api/affiliate").then(
      (res) =>
        res.data as {
          referral_count: number;
          affiliate_code: string;
          current_earnings: number;
          total_earnings: number;
          commission_rate: number;
        }
    );
    return data;
  });

  return (
    <div>
      <div className="grid grid-cols-5 gap-4 place-items-center">
        <button
          type="button"
          className="p-4 border border-white dark:border-theme-gray/20 rounded-full"
          onClick={() => {
            // Share to facebook
            const url = `https://www.facebook.com/sharer/sharer.php?u=${text}`;

            window.open(url, "_blank");
          }}
        >
          <Icons.facebook className="w-4 h-4 text-[#1877F2]" />
        </button>
        <button
          type="button"
          className="p-4 border border-white dark:border-theme-gray/20 rounded-full"
          onClick={() => {
            // Share to whatsapp
            const url = `https://wa.me/?text=${encodeURIComponent(text)}`;

            window.open(url, "_blank");
          }}
        >
          <Icons.whatsapp className="w-4 h-4 text-[#25D366]" />
        </button>
        <button
          type="button"
          className="p-4 border border-white dark:border-theme-gray/20 rounded-full"
          onClick={() => {
            // Share to telegram
            const url = `https://t.me/share/url?url=${text}`;

            window.open(url, "_blank");
          }}
        >
          <Icons.telegram className="w-4 h-4 text-[#26A5E4]" />
        </button>
        <button
          type="button"
          className="p-4 border border-white dark:border-theme-gray/20 rounded-full"
          onClick={() => {
            // Share to twitter
            const url = `https://twitter.com/intent/tweet?url=${text}`;

            window.open(url, "_blank");
          }}
        >
          <Icons.twitter className="w-4 h-4 text-[#1DA1F2]" />
        </button>
        <button
          type="button"
          className="p-4 border border-white dark:border-theme-gray/20 rounded-full"
          onClick={() => {
            // Share to twitter
            navigator.clipboard.writeText(text);

            toast.success("Link berhasil disalin");
          }}
        >
          <LinkIcon className="w-4 h-4 text-white dark:text-theme-accent" />
        </button>
      </div>
      {!user ? (
        <div className="flex pt-4 space-x-4">
          <AuthLink
            to="/sign-in"
            className="flex items-center justify-center w-full h-12 px-4 py-2 text-lg font-semibold text-white rounded-lg bg-theme-accent"
          >
            Join Affiliate
          </AuthLink>
        </div>
      ) : isSuccess ? (
        <div className="py-2">
          <Alert>
            <AlertDescription>
              Undang orang lain dan dapatkan komisi
              <span className="font-bold">{` ${affiliate.commission_rate}% `}</span>
              dari pembayaran mereka
            </AlertDescription>
          </Alert>
        </div>
      ) : null}
    </div>
  );
}
